import React, { useState, useContext, useEffect } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Switch, FormControlLabel, IconButton, Snackbar } from '@mui/material';
import { useParams } from 'react-router-dom';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { updateInvoice } from '../../graphql/mutations';
import { getPricelist, listPricelists, listParts } from '../../graphql/queries';
import { updatePart, createPart, deletePart } from '../../graphql/mutations';
import { AppContext } from '../../App';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

// Import the same components used in CreateInvoice
import InvoiceHeader from './InvoiceHeader';
import InvoiceStats from './InvoiceStats';
import PartsList from './PartsList';
import ActionButtons from './ActionButtons';
import { SearchModal, CustomPartModal, UnitByWeightModal } from './InvoiceModals';
import OurLoader from '../OurLoader';

// Custom query to get invoice with parts
const getInvoiceWithParts = /* GraphQL */ `
  query GetInvoice($id: ID!, $nextToken: String, $limit: Int) {
    getInvoice(id: $id) {
      id
      name
      invoiceNumber
      status
      ownerID
      recipientID
      partCount
      createdAt
      updatedAt
      parts(limit: $limit, nextToken: $nextToken) {
        items {
          id
          partNumber
          price
          grade
          status
          fromPricelist
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  }
`;

const EditInvoice = () => {
  const { invoiceId } = useParams();
  const { userRecord } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [formData, setFormData] = useState({
    contactName: '',
    companyName: '',
    email: '',
    priceList: '',
    invoiceNumber: '',
  });
  const [invoiceStats, setInvoiceStats] = useState({
    wholeUnits: 0,
    halfUnits: 0,
    totalUnits: 0,
    currentTotal: 0,
    averagePerUnit: 0
  });
  const [pricelistName, setPricelistName] = useState('');
  
  // State for modals
  const [searchOpen, setSearchOpen] = useState(false);
  const [customPartOpen, setCustomPartOpen] = useState(false);
  const [unitByWeightOpen, setUnitByWeightOpen] = useState(false);
  
  // State for sorting and filtering
  const [sortOption, setSortOption] = useState('newest');
  const [filterOption, setFilterOption] = useState('all');
  const [aggregateParts, setAggregateParts] = useState(false);
  const [showFullPartNumbers, setShowFullPartNumbers] = useState(true);

  // New state for pricelist dropdown
  const [pricelists, setPricelists] = useState([]);
  const [selectedPricelist, setSelectedPricelist] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [pricelistMap, setPricelistMap] = useState({});

  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // State for provider information
  const [providerInfo, setProviderInfo] = useState(null);

  const fetchInvoiceDetails = async (id) => {
    try {
      setLoading(true);
      
      // Fetch the invoice first
      const initialResponse = await API.graphql(
        graphqlOperation(getInvoiceWithParts, { id, limit: 100 })
      );
      
      const invoice = initialResponse.data.getInvoice;
      
      // Get the first page of parts
      let allParts = invoice.parts.items || [];
      let nextToken = invoice.parts.nextToken;
      
      // Fetch additional pages if there are more parts
      while (nextToken) {
        const additionalResponse = await API.graphql(
          graphqlOperation(getInvoiceWithParts, { id, nextToken, limit: 100 })
        );
        
        const additionalParts = additionalResponse.data.getInvoice.parts.items || [];
        allParts = [...allParts, ...additionalParts];
        nextToken = additionalResponse.data.getInvoice.parts.nextToken;
      }
      
      // Reconstruct the invoice object with all parts
      const completeInvoice = {
        ...invoice,
        parts: {
          items: allParts
        }
      };
      
      // Calculate statistics
      let wholeCount = 0;
      let halfCount = 0;
      let totalValue = 0;

      if (allParts.length > 0) {
        allParts.forEach(part => {
          if (part.grade === 1) wholeCount++;
          if (part.grade === 0.5) halfCount++;
        });
      }

      // Calculate averages with and without aftermarkets
      const partsForAverage = allParts.filter(p => p?.status !== 'empty' && p?.status !== 'aftermarket' && String(p?.partNumber || '').trim().toLowerCase() !== 'after market');
      const allPartsForAverage = allParts.filter(p => p?.status !== 'empty'); // Include aftermarkets but exclude empties
      const totalAllParts = allParts.reduce((sum, p) => sum + parseFloat(p.price || 0), 0);

      const stats = {
        wholeUnits: wholeCount,
        halfUnits: halfCount,
        totalUnits: wholeCount + halfCount,
        currentTotal: totalAllParts,
        averagePerUnit: (partsForAverage.reduce((sum, p) => sum + parseFloat(p.price || 0), 0)) / (partsForAverage.length || 1),
        averagePerUnitWithAftermarkets: (allPartsForAverage.reduce((sum, p) => sum + parseFloat(p.price || 0), 0)) / (allPartsForAverage.length || 1)
      };
      
      setInvoiceStats(stats);
      setCurrentInvoice(completeInvoice);

      // Fetch provider information from active pricelist
      await fetchProviderInfo(completeInvoice);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setLoading(false);
    }
  };

  // New function to fetch provider information from active pricelist
  const fetchProviderInfo = async (invoice) => {
    if (!userRecord?.activePricelistID) {
      // If no active pricelist, use invoice data as fallback
      setFormData({
        contactName: '',
        companyName: invoice?.name || '',
        invoiceNumber: invoice?.invoiceNumber || '',
        email: '',
        priceList: ''
      });
      return;
    }

    try {
      // Query to get pricelist with provider information
      const pricelistQuery = /* GraphQL */ `
        query GetPricelist($id: ID!) {
          getPricelist(id: $id) {
            id
            name
            provider {
              id
              contactName
              companyName
              email
            }
          }
        }
      `;

      const response = await API.graphql(
        graphqlOperation(pricelistQuery, { id: userRecord.activePricelistID })
      );
      
      const pricelist = response.data.getPricelist;
      
      if (pricelist && pricelist.provider) {
        const provider = pricelist.provider;
        setProviderInfo(provider);
        
        const formDataToSet = {
          contactName: provider.contactName || '',
          companyName: provider.companyName || '',
          invoiceNumber: invoice?.invoiceNumber || '',
          email: provider.email || '',
          priceList: pricelist.name || ''
        };
        setFormData(formDataToSet);
      } else {
        // Fallback to invoice data if no provider found
        const fallbackData = {
          contactName: '',
          companyName: invoice?.name || '',
          invoiceNumber: invoice?.invoiceNumber || '',
          email: '',
          priceList: pricelist?.name || ''
        };
        setFormData(fallbackData);
      }
    } catch (error) {
      console.error('Error fetching provider info:', error);
      // Fallback to invoice data
      const errorFallbackData = {
        contactName: '',
        companyName: invoice?.name || '',
        invoiceNumber: invoice?.invoiceNumber || '',
        email: '',
        priceList: ''
      };
      setFormData(errorFallbackData);
    }
  };

  // New function to fetch all pricelists for the user
  const fetchPricelists = async () => {
    if (!userRecord) return;
    
    try {
      // Fetch pricelists where user is the owner
      const ownedResponse = await API.graphql(
        graphqlOperation(listPricelists, { 
          filter: { userId: { eq: userRecord.id } },
          limit: 1000
        })
      );
      
      const ownedPricelists = ownedResponse.data.listPricelists.items || [];
      
      // Fetch all pricelists and filter client-side for providerId
      // Note: providerId is not available as a filter field in GraphQL schema
      const allResponse = await API.graphql(
        graphqlOperation(listPricelists, {
          limit: 1000
        })
      );
      
      const fetchedPricelists = allResponse.data.listPricelists.items || [];
      
      // Filter for pricelists where user is the provider (client-side filter)
      const providedPricelists = fetchedPricelists.filter(
        p => p && p.providerId === userRecord.id
      );
      
      // Combine and deduplicate by ID
      const allPricelists = [...ownedPricelists, ...providedPricelists].filter(p => p && p.id);
      const uniquePricelistsMap = new Map();
      
      allPricelists.forEach(pricelist => {
        if (!uniquePricelistsMap.has(pricelist.id)) {
          uniquePricelistsMap.set(pricelist.id, pricelist);
        }
      });
      
      // Sort pricelists by createdAt (newest first)
      const pricelistData = Array.from(uniquePricelistsMap.values())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setPricelists(pricelistData);
      
      // Create a map of pricelist IDs to names
      const nameMap = {};
      pricelistData.forEach(pricelist => {
        nameMap[pricelist.id] = pricelist.name;
      });
      setPricelistMap(nameMap);
      
      // Set active pricelist as default if available
      if (userRecord.activePricelistID) {
        setSelectedPricelist(userRecord.activePricelistID);
      } else if (pricelistData.length > 0) {
        setSelectedPricelist(pricelistData[0].id);
      }
    } catch (error) {
      console.error('Error fetching pricelists:', error);
    }
  };

  const fetchPricelistName = async () => {
    if (!userRecord?.activePricelistID) return;
    try {
      const response = await API.graphql(
        graphqlOperation(getPricelist, { id: userRecord.activePricelistID })
      );
      const pricelist = response.data.getPricelist;
      if (pricelist && pricelist.name) {
        setPricelistName(pricelist.name);
        
        // Add to the pricelist map
        setPricelistMap(prev => ({
          ...prev,
          [pricelist.id]: pricelist.name
        }));
      }
    } catch (error) {
      console.error('Error fetching pricelist name:', error);
    }
  };

  // New function to handle pricelist change and update part prices
  const handlePricelistChange = async (pricelistId) => {
    if (!currentInvoice || !pricelistId || updating) return;
    
    try {
      setUpdating(true);
      // Find the selected pricelist
      const selectedList = pricelists.find(p => p.id === pricelistId);
      if (!selectedList) {
        console.error('Selected pricelist not found');
        setUpdating(false);
        return;
      }

      setSelectedPricelist(pricelistId);

      // Get parts from the invoice
      const parts = currentInvoice.parts?.items || [];
      if (parts.length === 0) {
        setUpdating(false);
        return;
      }

      // Instead of querying for parts, get the pricelist data from S3
      console.log(`Getting pricelist data for ${selectedList.name} with s3Key: ${selectedList.s3Key}`);
      
      // Fetch the JSON data from S3
      const s3Response = await Storage.get(selectedList.s3Key, { download: true });
      const blob = await s3Response.Body.text();
      const pricelistData = JSON.parse(blob);
      
      console.log(`Found ${pricelistData.length} parts in pricelist ${selectedList.name}`);
      
      // Create a map of part numbers to prices from the pricelist
      const partPriceMap = {};
      pricelistData.forEach(item => {
        // CSVUpload.js maps CSV data to "PART NUMBER" and uses adjustedPrice
        const partNumber = item["PART NUMBER"];
        const price = item.adjustedPrice || item.PRICE;
        
        if (partNumber && price) {
          // Store both the original part number and a normalized version (lowercase, no spaces)
          partPriceMap[partNumber] = price;
          // Also store a normalized version of the part number for better matching
          partPriceMap[partNumber.toLowerCase().replace(/\s+/g, '')] = price;
        }
      });
      
      console.log(`Mapped ${Object.keys(partPriceMap).length} parts from pricelist`);

      // Update each part in the invoice with the new price
      let updatedCount = 0;
      let totalValue = 0;
      const updatePromises = parts.map(async (part) => {
        // Find the price for this part in the new pricelist
        // Try exact match first
        let newPrice = partPriceMap[part.partNumber];
        
        // If no exact match, try normalized match (lowercase, no spaces)
        if (newPrice === undefined) {
          const normalizedPartNumber = part.partNumber.toLowerCase().replace(/\s+/g, '');
          newPrice = partPriceMap[normalizedPartNumber];
          
          if (newPrice !== undefined) {
            console.log(`Found normalized match for ${part.partNumber}`);
          }
        }
        
        // If still no match, try more flexible matching
        if (newPrice === undefined) {
          const partNumberLower = part.partNumber.toLowerCase();
          
          // Try to find a key in the map that contains or is contained by the part number
          const matchingKey = Object.keys(partPriceMap).find(key => {
            const keyLower = key.toLowerCase();
            return keyLower.includes(partNumberLower) || 
                   partNumberLower.includes(keyLower);
          });
          
          if (matchingKey) {
            newPrice = partPriceMap[matchingKey];
            console.log(`Found flexible match for ${part.partNumber} -> ${matchingKey}`);
          }
        }
        
        if (newPrice !== undefined) {
          // Always update the fromPricelist reference, and update price if it's different
          const updateInput = {
            id: part.id,
            fromPricelist: pricelistId
          };
          
          // Only update price if it's different to avoid unnecessary updates
          if (parseFloat(newPrice) !== parseFloat(part.price)) {
            updateInput.price = newPrice;
          }
          
          console.log(`Updating part ${part.partNumber} with price $${newPrice} (old: $${part.price})`);
          
          await API.graphql(
            graphqlOperation(updatePart, {
              input: updateInput
            })
          );
          updatedCount++;
          return { ...part, price: newPrice, fromPricelist: pricelistId };
        } else {
          console.log(`No matching part found for ${part.partNumber} in the selected pricelist`);
          return part;
        }
      });

      // Wait for all updates to complete
      const updatedParts = await Promise.all(updatePromises);
      
      // Update the invoice with updated parts
      const updatedInvoice = {
        ...currentInvoice,
        parts: { items: updatedParts }
      };
      
      // Recalculate statistics
      // Recalculate totals/averages with and without aftermarkets
      const partsForAverage2 = updatedParts.filter(p => p?.status !== 'empty' && p?.status !== 'aftermarket' && String(p?.partNumber || '').trim().toLowerCase() !== 'after market');
      const allPartsForAverage2 = updatedParts.filter(p => p?.status !== 'empty'); // Include aftermarkets but exclude empties
      const totalAllParts2 = updatedParts.reduce((sum, p) => sum + parseFloat(p.price || 0), 0);

      const updatedStats = {
        ...invoiceStats,
        currentTotal: totalAllParts2,
        averagePerUnit: (partsForAverage2.reduce((sum, p) => sum + parseFloat(p.price || 0), 0)) / (partsForAverage2.length || 1),
        averagePerUnitWithAftermarkets: (allPartsForAverage2.reduce((sum, p) => sum + parseFloat(p.price || 0), 0)) / (allPartsForAverage2.length || 1)
      };
      
      setInvoiceStats(updatedStats);
      setCurrentInvoice(updatedInvoice);
      console.log(`Updated ${updatedCount} parts with prices from ${selectedList.name}`);
      
      // Store the pricelist name in the map for display
      setPricelistMap(prevMap => ({
        ...prevMap,
        [pricelistId]: selectedList.name
      }));
      
      // Refresh invoice details to ensure we have the latest data
      await fetchInvoiceDetails(invoiceId);
      
      setUpdating(false);
    } catch (error) {
      console.error('Error updating part prices:', error);
      setUpdating(false);
    }
  };

  // Function to get sorted and filtered parts
  const getSortedAndFilteredParts = (parts) => {
    if (!parts || !Array.isArray(parts)) return [];
    
    // Apply filter first
    let filteredParts = [...parts];
    if (filterOption === 'wholeUnits') {
      filteredParts = parts.filter(part => part.grade === 1);
    } else if (filterOption === 'halfUnits') {
      filteredParts = parts.filter(part => part.grade === 0.5);
    }
    
    // Apply aggregation if enabled
    let processedParts = filteredParts;
    if (aggregateParts) {
      const partMap = new Map();
      
      filteredParts.forEach(part => {
        const key = `${part.partNumber}-${part.grade}`;
        if (partMap.has(key)) {
          const existing = partMap.get(key);
          existing.quantity += 1;
          existing.totalPrice += parseFloat(part.price || 0);
        } else {
          partMap.set(key, {
            ...part,
            quantity: 1,
            totalPrice: parseFloat(part.price || 0)
          });
        }
      });
      
      processedParts = Array.from(partMap.values());
    }
    
    // Then apply sort
    switch (sortOption) {
      case 'newest':
        return processedParts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return processedParts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'partNumber':
        return processedParts.sort((a, b) => a.partNumber.localeCompare(b.partNumber));
      case 'priceHighToLow':
        return processedParts.sort((a, b) => (aggregateParts ? b.totalPrice - a.totalPrice : (b.price || 0) - (a.price || 0)));
      case 'priceLowToHigh':
        return processedParts.sort((a, b) => (aggregateParts ? a.totalPrice - b.totalPrice : (a.price || 0) - (b.price || 0)));
      default:
        return processedParts;
    }
  };

  // Update invoice data
  const handleUpdateInvoice = async (updatedData) => {
    try {
      const invoiceInput = {
        id: invoiceId,
        ...updatedData
      };

      const response = await API.graphql(
        graphqlOperation(updateInvoice, { input: invoiceInput })
      );
      
      const updatedInvoice = response.data.updateInvoice;
      setCurrentInvoice(updatedInvoice);
    } catch (error) {
      console.error('Error updating invoice:', error);
    }
  };

  // Handler to duplicate a part (increment quantity)
  const handleIncrementPart = async (part) => {
    if (!part || updating) return;
    
    setUpdating(true);
    setUpdateMessage("Adding part...");
    
    try {
      // Create a new part with the same details
      const partToAdd = {
        partNumber: part.partNumber,
        price: Math.round(parseFloat(
          (part && (
            part.groupAdjustedPrice ??
            part.adjustedPrice ??
            part["PRICE"] ??
            part.price ??
            part.minPrice
          )) || 0
        )),
        fromPricelist: part.fromPricelist || selectedPricelist,
        grade: part.grade,
        status: part.status,
        invoicePartsId: invoiceId
      };

      await API.graphql(
        graphqlOperation(createPart, { input: partToAdd })
      );

      // Update invoice part count - use the current invoice part count plus 1
      const updatedPartCount = ((currentInvoice?.parts?.items?.length || 0) + 1);
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: invoiceId,
            partCount: updatedPartCount
          }
        })
      );

      setUpdateMessage("Part added successfully");
      setSnackbarOpen(true);
      fetchInvoiceDetails(invoiceId);
    } catch (error) {
      console.error("Error duplicating part:", error);
      setUpdateMessage("Error adding part");
      setSnackbarOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  // Handler to remove a part (decrement quantity)
  const handleDecrementPart = async (part) => {
    if (!part || updating) return;
    
    setUpdating(true);
    setUpdateMessage("Removing part...");
    
    try {
      // Delete the part
      await API.graphql(
        graphqlOperation(deletePart, {
          input: { id: part.id }
        })
      );

      // Update invoice part count - use the current invoice part count minus 1
      const updatedPartCount = Math.max(0, (currentInvoice?.parts?.items?.length || 0) - 1);
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: invoiceId,
            partCount: updatedPartCount
          }
        })
      );

      setUpdateMessage("Part removed successfully");
      setSnackbarOpen(true);
      fetchInvoiceDetails(invoiceId);
    } catch (error) {
      console.error("Error removing part:", error);
      setUpdateMessage("Error removing part");
      setSnackbarOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  // Add this before the return statement
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to get paginated parts
  const getPaginatedParts = (parts) => {
    const sortedAndFiltered = getSortedAndFilteredParts(parts);
    if (sortedAndFiltered.length <= rowsPerPage) {
      return sortedAndFiltered;
    }
    
    return sortedAndFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails(invoiceId);
      fetchPricelistName();
      fetchPricelists();
    }
  }, [invoiceId, userRecord?.activePricelistID]);

  // Share aggregation state through window object for PDF generation
  useEffect(() => {
    window.aggregateParts = aggregateParts;
    window.showFullPartNumbers = showFullPartNumbers;
    
    return () => {
      window.aggregateParts = null;
      window.showFullPartNumbers = null;
    };
  }, [aggregateParts, showFullPartNumbers]);

  if (loading) {
    return <OurLoader />;
  }

  return (
    <Box sx={{ 
      backgroundColor: '#000',
      minHeight: '100vh',
      padding: '20px',
      marginTop: '20px',
    }}>
      {currentInvoice && (
        <>
          {/* Pricelist Selector */}
          <Box sx={{ 
            backgroundColor: '#1a1a1a',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '15px'
          }}>
            <Typography variant="h6" sx={{ color: '#fff', marginBottom: '10px' }}>
              Update Invoice Part Prices
            </Typography>
            <FormControl fullWidth variant="outlined" sx={{ marginBottom: '10px' }}>
              <InputLabel id="pricelist-select-label" sx={{ color: '#aaa' }}>
                Select Pricelist
              </InputLabel>
              <Select
                labelId="pricelist-select-label"
                id="pricelist-select"
                value={selectedPricelist}
                onChange={(e) => handlePricelistChange(e.target.value)}
                label="Select Pricelist"
                disabled={updating || pricelists.length === 0}
                sx={{ 
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#444',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#666',
                  },
                }}
              >
                {pricelists.map((pricelist) => (
                  <MenuItem key={pricelist.id} value={pricelist.id}>
                    {pricelist.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {updating && (
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Updating part prices...
              </Typography>
            )}
          </Box>

          {/* Invoice Header */}
          <InvoiceHeader formData={formData} />

          {/* Stats Box */}
          <InvoiceStats invoiceStats={invoiceStats} />

          {/* Parts List */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            marginBottom: '10px',
            paddingRight: '10px'
          }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={aggregateParts}
                  onChange={(e) => setAggregateParts(e.target.checked)}
                  color="primary"
                />
              }
              label="Aggregate Parts"
              sx={{ color: '#fff' }}
            />
            <FormControlLabel
              control={
                <Switch 
                  checked={showFullPartNumbers}
                  onChange={(e) => setShowFullPartNumbers(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Full Part Numbers"
              sx={{ color: '#fff', marginLeft: '10px' }}
            />
          </Box>
           {/* Action Buttons */}
           <ActionButtons 
            setSearchOpen={setSearchOpen}
            setCustomPartOpen={setCustomPartOpen}
            setUnitByWeightOpen={setUnitByWeightOpen}
            currentInvoice={currentInvoice}
            formData={formData}
            invoiceStats={invoiceStats}
            aggregateParts={aggregateParts}
            showFullPartNumbers={showFullPartNumbers}
            onAddEmptyPart={async () => {
              try {
                const input = {
                  partNumber: 'Empty',
                  price: 0,
                  grade: 0,
                  status: 'empty',
                  addedByUser: userRecord.id,
                  fromPricelist: userRecord.activePricelistID,
                  invoicePartsId: currentInvoice.id,
                };
                await API.graphql(graphqlOperation(createPart, { input }));
                await API.graphql(
                  graphqlOperation(updateInvoice, {
                    input: { id: currentInvoice.id, partCount: (currentInvoice.partCount || 0) + 1 }
                  })
                );
                fetchInvoiceDetails(invoiceId);
              } catch (e) {
                console.error('Error adding empty part:', e);
              }
            }}
            onAddAftermarketPart={async () => {
              try {
                const plResp = await API.graphql(
                  graphqlOperation(getPricelist, { id: userRecord.activePricelistID })
                );
                const s3Key = plResp?.data?.getPricelist?.s3Key;
                if (!s3Key) throw new Error('Pricelist S3 key not found');
                const file = await Storage.get(s3Key, { download: true });
                const text = await file.Body.text();
                const json = JSON.parse(text);
                const item = (json || []).find(
                  (row) => String(row['PART NUMBER'] || '').trim().toLowerCase() === 'after market'
                );
                const basePrice = item ? (item.adjustedPrice ?? item.PRICE ?? 0) : 0;
                const price = Math.round(parseFloat(basePrice || 0));

                const input = {
                  partNumber: 'After Market',
                  price: isNaN(price) ? 0 : price,
                  grade: 1,
                  status: 'aftermarket',
                  addedByUser: userRecord.id,
                  fromPricelist: userRecord.activePricelistID,
                  invoicePartsId: currentInvoice.id,
                };
                await API.graphql(graphqlOperation(createPart, { input }));
                await API.graphql(
                  graphqlOperation(updateInvoice, {
                    input: { id: currentInvoice.id, partCount: (currentInvoice.partCount || 0) + 1 }
                  })
                );
                fetchInvoiceDetails(invoiceId);
              } catch (e) {
                console.error('Error adding aftermarket part:', e);
              }
            }}
          />
          
          <PartsList 
            currentInvoice={currentInvoice}
            getSortedAndFilteredParts={getSortedAndFilteredParts}
            getPaginatedParts={getPaginatedParts}
            sortOption={sortOption}
            setSortOption={setSortOption}
            filterOption={filterOption}
            setFilterOption={setFilterOption}
            fetchInvoiceDetails={() => fetchInvoiceDetails(invoiceId)}
            pricelistMap={pricelistMap}
            aggregateParts={aggregateParts}
            showFullPartNumbers={showFullPartNumbers}
            handleIncrementPart={handleIncrementPart}
            handleDecrementPart={handleDecrementPart}
            updating={updating}
            // Pagination props
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={currentInvoice?.parts?.items?.length || 0}
          />

         

          {/* Modals */}
          <SearchModal 
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            pricelistName={pricelistName}
            currentInvoice={currentInvoice}
            userRecord={userRecord}
            fetchInvoiceDetails={() => fetchInvoiceDetails(invoiceId)}
          />
          
          <CustomPartModal 
            open={customPartOpen}
            onClose={() => setCustomPartOpen(false)}
            currentInvoice={currentInvoice}
            userRecord={userRecord}
            fetchInvoiceDetails={() => fetchInvoiceDetails(invoiceId)}
          />
          
          <UnitByWeightModal 
            open={unitByWeightOpen}
            onClose={() => setUnitByWeightOpen(false)}
            currentInvoice={currentInvoice}
            userRecord={userRecord}
            fetchInvoiceDetails={() => fetchInvoiceDetails(invoiceId)}
          />

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={updateMessage}
          />
        </>
      )}
    </Box>
  );
};

export default EditInvoice;
